import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Users, Settings } from 'lucide-react';
import { CategoryManagerUI } from './CategoryManagerUI';
import type { TechTennisConfig } from '../types/competition';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  competitionType: 'RANKING' | 'TOURNAMENT';
  initialConfig?: TechTennisConfig;
  onSave: (config: TechTennisConfig) => void;
  initialExpandedCategory?: string | null;
}

export const CompetitionConfigModal: React.FC<Props> = ({ isOpen, onClose, competitionType, initialConfig, onSave, initialExpandedCategory }) => {
  const [classifications, setClassifications] = useState<any[]>([]);
  
  useEffect(() => {
    if (isOpen) {
      const token = localStorage.getItem('tt_auth_token');
      const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3001";
      fetch(`${API_URL}/api/classifications`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      .then(res => res.json())
      .then(data => {
        setClassifications(data);
      })
      .catch(console.error);
      
      if (initialConfig) {
        setConfig({
          ...initialConfig,
          matchEngine: initialConfig.matchEngine || {
            scoreFormat: 'BEST_OF_3_REGULAR',
            advantageRule: 'WITH_ADVANTAGE',
            walkoverRule: 'SCORE_6_0_6_0'
          },
          pointsConfig: initialConfig.pointsConfig || {
            winner: 10,
            loser: 2,
            woWinner: 10,
            woLoser: -5
          },
          resetPolicy: initialConfig.resetPolicy || { policy: 'MANUAL_ONLY' },
          ...(competitionType === 'RANKING' ? {
            ladderRules: {
              challengeWindowAbove: 3,
              challengeWindowBelow: 0,
              impactLogic: 'LEAPFROG_PUSH',
              acceptDeadlineDays: 3,
              maxRefusalsAllowed: 2,
              inactivityDaysLimit: 30,
              inactivityPenalty: 'DROPS_X_POSITIONS',
              inactivityPenaltyPositions: 2,
              rematchCooldownDays: 7,
              matchmakingStrategy: 'PLAYER_SLOT_BOOKING',
              matchGenerationMode: 'MANAGER_ONLY',
              ...(initialConfig.ladderRules || {})
            }
          } : {
            tournamentRules: {
              drawFormat: 'SINGLE_ELIMINATION',
              tiebreakerPriority: ['WINS', 'HEAD_TO_HEAD', 'SETS_DIFFERENCE'],
              participantType: 'SINGLES',
              matchGenerationMode: 'MANAGER_ONLY',
              stagePoints: {
                champion: 100, finalist: 60, semiFinalist: 36, quarterFinalist: 18, roundOf16: 9, roundOf32: 0, roundOf64: 0
              },
              ...(initialConfig.tournamentRules || {})
            }
          })
        });
      }
    }
  }, [isOpen, initialConfig]);

  const [config, setConfig] = useState<TechTennisConfig>(() => {
    const defaultMatchEngine = {
      scoreFormat: 'BEST_OF_3_REGULAR',
      advantageRule: 'WITH_ADVANTAGE',
      walkoverRule: 'SCORE_6_0_6_0'
    } as const;
    const defaultPointsConfig = {
      winner: 10,
      loser: 2,
      woWinner: 10,
      woLoser: -5
    };
    
    if (initialConfig) {
      return {
        ...initialConfig,
        matchEngine: initialConfig.matchEngine || defaultMatchEngine,
        pointsConfig: initialConfig.pointsConfig || defaultPointsConfig,
        resetPolicy: initialConfig.resetPolicy || { policy: 'MANUAL_ONLY' },
        ...(competitionType === 'RANKING' ? {
          ladderRules: {
            challengeWindowAbove: 3,
            challengeWindowBelow: 0,
            impactLogic: 'LEAPFROG_PUSH',
            acceptDeadlineDays: 3,
            maxRefusalsAllowed: 2,
            inactivityDaysLimit: 30,
            inactivityPenalty: 'DROPS_X_POSITIONS',
            inactivityPenaltyPositions: 2,
            rematchCooldownDays: 7,
            matchmakingStrategy: 'PLAYER_SLOT_BOOKING',
            matchGenerationMode: 'MANAGER_ONLY',
            ...(initialConfig.ladderRules || {})
          }
        } : {
          tournamentRules: {
            drawFormat: 'SINGLE_ELIMINATION',
            tiebreakerPriority: ['WINS', 'HEAD_TO_HEAD', 'SETS_DIFFERENCE'],
            participantType: 'SINGLES',
            matchGenerationMode: 'MANAGER_ONLY',
            stagePoints: {
              champion: 100, finalist: 60, semiFinalist: 36, quarterFinalist: 18, roundOf16: 9, roundOf32: 0, roundOf64: 0
            },
            ...(initialConfig.tournamentRules || {})
          }
        })
      };
    }
    return {
      matchEngine: defaultMatchEngine,
      pointsConfig: defaultPointsConfig,
      resetPolicy: {
        policy: 'MANUAL_ONLY'
      },
      ...(competitionType === 'RANKING' ? {
        ladderRules: {
          challengeWindowAbove: 3,
          challengeWindowBelow: 0,
          impactLogic: 'LEAPFROG_PUSH',
          acceptDeadlineDays: 3,
          maxRefusalsAllowed: 2,
          inactivityDaysLimit: 30,
          inactivityPenalty: 'DROPS_X_POSITIONS',
          inactivityPenaltyPositions: 2,
          rematchCooldownDays: 7,
          matchmakingStrategy: 'PLAYER_SLOT_BOOKING'
        }
      } : {
        tournamentRules: {
          drawFormat: 'SINGLE_ELIMINATION',
          tiebreakerPriority: ['WINS', 'HEAD_TO_HEAD', 'SETS_DIFFERENCE'],
          participantType: 'SINGLES',
          matchGenerationMode: 'MANAGER_ONLY',
          stagePoints: {
            champion: 100,
            finalist: 60,
            semiFinalist: 36,
            quarterFinalist: 18,
            roundOf16: 9,
            roundOf32: 0,
            roundOf64: 0
          },
          stagePointsSecondary: {
            champion: 0,
            finalist: 0,
            semiFinalist: 0,
            quarterFinalist: 0,
            roundOf16: 0,
            roundOf32: 0,
            roundOf64: 0
          }
        },
      })
    };
  });

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(0,0,0,0.4)",
          backdropFilter: "blur(4px)",
          zIndex: 1000,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px",
        }}
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          onClick={(e) => e.stopPropagation()}
          style={{
            width: "100%",
            maxWidth: "700px",
            backgroundColor: "#1A112C",
            backdropFilter: "blur(16px)",
            border: "1px solid rgba(255,255,255,0.2)",
            borderRadius: "8px",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            boxShadow: "0 20px 40px rgba(0,0,0,0.3)",
            maxHeight: "90vh",
          }}
        >
          {/* Header */}
          <div
            style={{
              background: "#6136B3",
              padding: "20px 24px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Settings size={28} color="white" />
              <div>
                <h2 style={{ color: "white", fontSize: "20px", fontWeight: 700, margin: 0 }}>
                  Configurações Avançadas
                </h2>
                <p style={{ color: "rgba(255, 255, 255, 0.7)", margin: 0, fontSize: "14px", fontWeight: 600 }}>
                  {competitionType === 'RANKING' ? 'Regras do Ranking Contínuo' : 'Regras do Torneio'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              style={{ background: "none", border: "none", color: "white", cursor: "pointer", display: "flex" }}
            >
              <X size={24} />
            </button>
          </div>

          {/* Body */}
          <div style={{ padding: '24px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: '32px' }}>
            
            {/* Configurações Gerais */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--primary-color)', marginBottom: '16px', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '8px' }}>
                <Users size={20} />
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: 'white' }}>Configurações Gerais</h3>
              </div>
              <CategoryManagerUI 
                categories={config.categories || []}
                categoriesConfig={config.categoriesConfig || {}}
                onUpdate={(newCategories, newCategoriesConfig) => {
                  setConfig({ ...config, categories: newCategories, categoriesConfig: newCategoriesConfig });
                }}
                competitionType={competitionType}
                classifications={classifications}
                initialExpandedCategory={initialExpandedCategory}
              />
            </div>

          </div>

          {/* Footer */}
          <div style={{ padding: '20px 24px', background: 'rgba(0,0,0,0.2)', borderTop: '1px solid rgba(255,255,255,0.1)', display: 'flex', justifyContent: 'flex-end', gap: '16px' }}>
            <button 
              onClick={onClose}
              style={{ padding: '10px 20px', background: 'transparent', color: 'white', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px', fontWeight: 600, cursor: 'pointer', transition: 'background 0.2s' }}
            >
              Cancelar
            </button>
            <button 
              onClick={() => { 
                onSave(config); 
                onClose(); 
              }}
              style={{ padding: '10px 20px', background: 'var(--primary-color)', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', transition: 'transform 0.2s' }}
            >
              Salvar Configurações
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};
